/**
 * Capability-drawer step rail — clickable pill row showing progress
 * through the Targets → Configure → Execution wizard. Used only when
 * the pipeline editor is active; legacy stacked-steps mode keeps the
 * existing DrawerStep components.
 *
 * Click semantics:
 *   - Always navigable backward.
 *   - Forward navigation only allowed when every prior step is valid
 *     (mirrors the "Next" button's disabled state).
 */

import { CheckIcon } from '@heroicons/react/24/solid';
import { Fragment } from 'react';

import { cn } from '@/lib/utils';

export interface StepRailStep {
  id: string;
  title: string;
  isValid: boolean;
}

interface StepRailProps {
  steps: StepRailStep[];
  currentIndex: number;
  onStepClick: (index: number) => void;
}

export function StepRail({ steps, currentIndex, onStepClick }: StepRailProps) {
  return (
    <nav
      aria-label="Capability run steps"
      className="flex items-center gap-1.5 overflow-x-auto"
    >
      {steps.map((step, i) => {
        const isActive = i === currentIndex;
        const isComplete = step.isValid && !isActive;
        const allPriorValid = steps.slice(0, i).every(s => s.isValid);
        const clickable = i <= currentIndex || allPriorValid;

        return (
          <Fragment key={step.id}>
            {i > 0 && (
              <div
                aria-hidden="true"
                className={cn(
                  'h-px w-4 shrink-0 transition-colors',
                  steps[i - 1].isValid
                    ? 'bg-chariot-brand-primary/60'
                    : 'bg-chariot-stroke'
                )}
              />
            )}
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(i)}
              aria-current={isActive ? 'step' : undefined}
              className={cn(
                'group flex shrink-0 items-center gap-2 rounded-full py-1 pr-3 pl-1 transition-colors',
                'focus-visible:ring-chariot-brand-primary focus:outline-hidden focus-visible:ring-2',
                isActive && 'bg-chariot-brand-primary/15',
                !isActive &&
                  clickable &&
                  'hover:bg-chariot-background-secondary cursor-pointer',
                !clickable && 'cursor-not-allowed opacity-60'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-6 items-center justify-center rounded-full text-[11px] font-bold transition-colors',
                  isActive && 'bg-chariot-brand-primary text-white',
                  isComplete &&
                    'bg-chariot-brand-primary/30 text-chariot-brand-primary',
                  !isActive &&
                    !isComplete &&
                    'border-chariot-stroke text-chariot-text-tertiary border'
                )}
              >
                {isComplete ? <CheckIcon className="size-3.5" /> : i + 1}
              </span>
              <span
                className={cn(
                  'text-xs font-medium whitespace-nowrap',
                  isActive && 'text-chariot-text-primary',
                  !isActive && isComplete && 'text-chariot-text-secondary',
                  !isActive && !isComplete && 'text-chariot-text-tertiary'
                )}
              >
                {step.title}
              </span>
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}
